import React, { useState, useEffect } from 'react';
import { totpService } from '../../services/totpService';

interface TwoFactorAuthSetupProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

const TwoFactorAuthSetup: React.FC<TwoFactorAuthSetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<'password' | 'qr' | 'verify'>('password');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrCodeDataUri, setQrCodeDataUri] = useState('');
  const [secret, setSecret] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await totpService.register(password);
      setQrCodeDataUri(result.qrCodeDataUri);
      setSecret(result.secret);
      setManualEntryKey(result.manualEntryKey);
      setStep('qr');
      setPassword(''); // Clear password for security
    } catch (err: any) {
      setError(err.message || 'Failed to register 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await totpService.verifyAndEnable(otpCode);
      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify OTP code');
    } finally {
      setLoading(false);
    }
  };

  const handleReRegister = async () => {
    setStep('password');
    setQrCodeDataUri('');
    setSecret('');
    setManualEntryKey('');
    setOtpCode('');
    setError('');
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-sm p-8">
      <div className="text-center mb-6">
        <div className="mx-auto h-16 w-16 bg-zazoo-accent-blue rounded-full flex items-center justify-center mb-4">
          <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-nunito font-bold text-gray-900">Enable Two-Factor Authentication</h2>
        <p className="mt-2 text-sm font-nunito text-gray-600">
          Secure your account with Google Authenticator
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 bg-zazoo-red bg-opacity-10 border border-zazoo-red rounded-lg mb-6">
          <svg className="w-5 h-5 text-zazoo-red flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p className="font-nunito text-zazoo-red text-sm">{error}</p>
        </div>
      )}

      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit} className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
              Enter your password to continue
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue focus:border-transparent"
              placeholder="Enter your password"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !password}
              className="flex-1 flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-mukta font-medium text-white bg-zazoo-accent-blue hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-accent-blue disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {loading ? 'Processing...' : 'Continue'}
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-mukta font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {step === 'qr' && (
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-sm font-nunito text-gray-700 mb-4">
              Scan this QR code with your Google Authenticator app
            </p>
            {qrCodeDataUri && (
              <div className="flex justify-center mb-4">
                <img src={qrCodeDataUri} alt="QR Code" className="w-64 h-64 border-2 border-gray-200 rounded-lg" />
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-xs font-nunito font-semibold text-gray-700 mb-2">Or enter this code manually:</p>
              <p className="text-sm font-mukta font-mono text-gray-900 break-all">{secret}</p>
            </div>
            <p className="text-xs font-nunito text-gray-500">
              After scanning, click Continue to verify your setup
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setStep('verify')}
              className="flex-1 flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-mukta font-medium text-white bg-zazoo-accent-blue hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-accent-blue transition-opacity"
            >
              Continue
            </button>
            <button
              onClick={handleReRegister}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-mukta font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Start Over
            </button>
          </div>
        </div>
      )}

      {step === 'verify' && (
        <form onSubmit={handleVerifySubmit} className="space-y-6">
          <div>
            <label htmlFor="otpCode" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
              Enter the 6-digit code from your Google Authenticator app
            </label>
            <input
              id="otpCode"
              type="text"
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpCode(value);
              }}
              required
              disabled={loading}
              maxLength={6}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue focus:border-transparent"
              placeholder="000000"
              autoComplete="one-time-code"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              className="flex-1 flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-mukta font-medium text-white bg-zazoo-accent-blue hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-accent-blue disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </button>
            <button
              type="button"
              onClick={() => setStep('qr')}
              disabled={loading}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-mukta font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default TwoFactorAuthSetup;

